/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useState, useCallback } from "react";
import { Typography, Button, Dialog, CircularProgress } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import SyncIcon from "@material-ui/icons/Sync";
import WarningSharp from "@material-ui/icons/WarningRounded";
import IconButton from "@material-ui/core/IconButton";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import { useExternalContacts } from "../../../context/externalContacts";
import { Link } from "react-router-dom";
import EnhancedTable, { getPhoneNormalized } from "./AzureContactsTable";

/**
 * Displays a dialog where a user can select the Users from Azure Active Directory, and save as contacts.
 */
export const ExternalContactsAzureSync = () => {
	const {
		contacts: existingContacts,
		createContacts,
		fetchAzureAdUsers,
		azureAdUsers: azureGroupsUsers,
		azureAdGroups,
		azureAdUsersLoading: isAzureDataLoading,
		azureAdLoadError,
		isAzureConfigLoading,
		azureConfig,
	} = useExternalContacts();

	const [open, setOpen] = useState(false);
	const [selectedUsers, setSelectedUsers] = useState([]);
	const [isSaving, setIsSaving] = useState(false);
	const [isLatestData, setIsLatestData] = useState(false);

	// Filtering already imported contacts.
	const { usersLookup, groupsLookup } = azureGroupsUsers.reduce(
		(prevVal, curVal) => {
			if (!existingContacts.find((user) => user.contactId === curVal.id)) {
				if (prevVal.usersLookup[curVal.id]) {
					prevVal.usersLookup[curVal.id].groups?.push(curVal.group);
				} else {
					prevVal.usersLookup[curVal.id] = { ...curVal, groups: [curVal.group] };
				}
				if (!prevVal.groupsLookup.includes(curVal.group)) {
					prevVal.groupsLookup.push(curVal.group);
				}
			}
			return prevVal;
		},
		{ usersLookup: {}, groupsLookup: [] },
	);

	const isTaskInProgress = isAzureConfigLoading || isAzureDataLoading;

	const hasAzureConfig =
		azureConfig?.TeamsSyncTenantId && azureConfig?.TeamsSyncClientId && azureConfig?.TeamsSyncClientSecret;

	const hasError = !isTaskInProgress && (azureAdLoadError || !hasAzureConfig);

	const createContactsHandler = useCallback(async () => {
		try {
			setIsSaving(true);
			const savingContactIds = [];
			const contactsToSave = Object.values(usersLookup)
				?.filter((user) => selectedUsers.includes(user.id))
				?.map(({ id, displayName, businessPhones, mobilePhone, groups }) => {
					savingContactIds.push(id);
					const phone = getPhoneNormalized({ mobilePhone, businessPhones });
					return {
						azureAdUserId: id,
						contactId: id,
						fullName: displayName,
						phone,
						tags: groups,
					};
				});
			await createContacts(contactsToSave);
			setSelectedUsers((currentUsers) => currentUsers.filter((user) => !savingContactIds.includes(user)));
		} catch (_) {}
		setIsSaving(false);
	}, [selectedUsers, usersLookup, createContacts]);

	const initializeAzureSync = async () => {
		setOpen(true);
		if (hasAzureConfig && !azureAdGroups.length && !azureGroupsUsers.length) {
			setIsLatestData(true);
			await fetchAzureAdUsers(false);
		} else {
			setIsLatestData(false);
		}
	};

	const syncAzureUsers = async () => {
		setIsSaving(true);
		await fetchAzureAdUsers(true);
		setIsLatestData(true);
		setIsSaving(false);
	};

	return (
		<div>
			<Button variant="outlined" size="small" onClick={initializeAzureSync} startIcon={<SyncIcon />}>
				Sync Azure AD
			</Button>

			<Dialog
				fullWidth
				maxWidth="md"
				open={open}
				onClose={() => {
					setOpen(false);
				}}
			>
				<AppBar color="default" elevation={2} position="sticky" style={{ padding: 0 }}>
					<Toolbar style={{ justifyContent: "space-between" }}>
						<div style={{ width: 145 }}>
							<IconButton
								edge="start"
								color="inherit"
								style={{ float: "left" }}
								size="small"
								onClick={() => {
									setOpen(false);
								}}
								aria-label="close"
							>
								<CloseIcon />
							</IconButton>
						</div>
						<Typography variant="h6">Import contacts from Azure AD</Typography>
						<div style={{ minWidth: 150 }}>
							<Button
								size="small"
								className="mr-2"
								title="Click to reload the users"
								variant="contained"
								color="secondary"
								disabled={isLatestData || isSaving}
								onClick={syncAzureUsers}
							>
								<SyncIcon />
							</Button>
							<Button
								size="small"
								variant="contained"
								color="secondary"
								disabled={!!!selectedUsers.length || isSaving}
								onClick={createContactsHandler}
							>
								Import Contact(s)
							</Button>
						</div>
					</Toolbar>
				</AppBar>
				<div style={{ padding: "0 30px", margin: "20px 0 20px 0" }}>
					<div>
						<div style={{ margin: "0 0 20px 0" }}>
							{isTaskInProgress ? (
								<div className="text-center mt-3">
									<CircularProgress />
									<p
										className="text-muted  line-height-3 container my-4"
										style={{ display: "inline-block", lineHeight: 2 }}
									>
										Loading...
										<br /> Please wait while we load the Azure AD users and groups.
									</p>
								</div>
							) : hasError ? (
								!hasAzureConfig ? (
									<p
										className="text-muted text-center line-height-3 container my-4"
										style={{ display: "inline-block", lineHeight: 2.5 }}
									>
										<WarningSharp /> Azure AD configuration was not found.
										<br /> Please configure the <Link to="/settings/integrations?tab=Teams">
											Azure AD integration{" "}
										</Link>{" "}
										and refresh this page.
									</p>
								) : azureAdLoadError ? (
									<div className="d-flex flex-row justify-content-center">
										<WarningSharp /> <p className="text-muted ml-2 my-auto">{azureAdLoadError}</p>
									</div>
								) : (
									""
								)
							) : (
								<>
									<p className="text-muted ml-2">
										Import external contacts from Azure Active Directory into PACE. Please select the users from the
										below table to import as contacts.
									</p>
									<EnhancedTable
										usersLookup={Object.values(usersLookup)}
										groupsLookup={groupsLookup}
										isSaving={isSaving}
										selectedUsers={selectedUsers}
										setSelectedUsers={setSelectedUsers}
									/>
								</>
							)}
						</div>
					</div>
				</div>
			</Dialog>
		</div>
	);
};
